import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Eye, PencilSimple, Plus, PaperPlaneTilt, CheckCircle, LockKey, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BasicInfoForm, { emptyBasicInfo, withStableKey } from "@/pages/dashboard/BasicInfoForm";

export default function BasicInfo() {
  const { user } = useAuth();
  const isStaff = user?.role && user.role !== "client";
  const canEditClient = ["admin", "cpa", "document_team"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // form object or null
  const [form, setForm] = useState(emptyBasicInfo());

  const [viewing, setViewing] = useState(null);
  const [submitConfirm, setSubmitConfirm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = isStaff ? "/basic-info" : "/basic-info/me";
      const { data } = await api.get(url);
      setItems(Array.isArray(data) ? data : (data ? [data] : []));
    } catch (e) {
      if (e?.response?.status !== 404) console.warn("basic-info load failed:", e);
    } finally {
      setLoading(false);
    }
  }, [isStaff]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyBasicInfo(),
      full_name: user?.name || "",
      email: user?.email || "",
    });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    // Client cannot edit once submitted; caller has view-only role.
    if (!isStaff && item.is_submitted) {
      toast.info("This form has been submitted. Please contact your CPA to update it.");
      return;
    }
    if (isStaff && !canEditClient) {
      toast.info("Only admin, CPA or document team can edit client details.");
      return;
    }
    setEditing(item);
    setForm({ ...emptyBasicInfo(), ...item, dependents: (item.dependents || []).map(withStableKey) });
    setDialogOpen(true);
  };

  const doSubmit = async () => {
    const payload = { ...form, is_submitted: true };
    try {
      if (editing) {
        await api.patch(`/basic-info/${editing.id}`, payload);
        toast.success("Basic Info form submitted");
      } else {
        await api.post("/basic-info", payload);
        toast.success("Basic Info form submitted");
      }
      setSubmitConfirm(false);
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
      setSubmitConfirm(false);
    }
  };

  const YearBadge = ({ year }) => (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-[#0A2540] text-white text-xs font-mono font-bold px-2.5 py-1">
      TY {year}
    </span>
  );

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="basic-info-title">
            {isStaff ? "Client Information Forms" : "Your Tax Information"}
          </h1>
          <p className="text-slate-600 mt-1">
            {isStaff
              ? "Every US Tax Client Information Form submitted across the firm."
              : "Submit your intake once per tax year. Once submitted, your CPA reviews and locks in your data."}
          </p>
        </div>
        {(canEditClient || (!isStaff && !items.some((it) => it.is_submitted))) && (
          <Button className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" onClick={openCreate} data-testid="create-basic-info-btn">
            <Plus size={16} className="mr-1.5" /> {isStaff ? "Add form for client" : "Create new form"}
          </Button>
        )}
      </div>

      {/* EMPTY STATE — client with zero submissions */}
      {!loading && items.length === 0 && !isStaff && (
        <div className="rounded-lg border tf-hairline bg-white p-12 text-center">
          <User size={48} weight="duotone" className="text-[#0A2540] mx-auto" />
          <h2 className="mt-4 font-[Chivo] text-xl font-black text-[#0A2540]">Get started with your tax intake</h2>
          <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
            Fill out the US Tax Client Information Form for this tax year. Your CPA will use it to prepare your return.
          </p>
          <Button className="mt-6 bg-[#D32F2F] hover:bg-[#D32F2F]/90" onClick={openCreate} data-testid="empty-create-btn">
            <Plus size={16} className="mr-1.5" /> Create your first form
          </Button>
        </div>
      )}

      {/* LIST */}
      {items.length > 0 && (
        <div className="space-y-3" data-testid="basic-info-list">
          {items.map((it) => {
            const submitted = !!it.is_submitted;
            const clientLocked = !isStaff && submitted;
            return (
              <div
                key={it.id}
                className="rounded-md border tf-hairline bg-white p-4 flex flex-wrap items-center gap-4"
                data-testid={`basic-info-card-${it.id}`}
              >
                <YearBadge year={it.tax_year} />
                <div className="flex-1 min-w-[200px]">
                  <div className="font-[Chivo] font-bold text-[#0A2540]">
                    {it.full_name || it.owner_name || "Untitled form"}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {isStaff && it.owner_name && <span>{it.owner_name} · </span>}
                    {submitted
                      ? <>Submitted {(it.submitted_at || it.updated_at || "").split("T")[0]}</>
                      : <>Draft — last edited {(it.updated_at || "").split("T")[0]}</>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {submitted ? (
                    <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <CheckCircle size={12} weight="fill" /> Submitted
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200">
                      Draft
                    </span>
                  )}
                  {clientLocked && (
                    <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest px-2 py-1 rounded bg-slate-100 text-slate-600 border border-slate-200">
                      <LockKey size={12} /> Locked
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="View" onClick={() => setViewing(it)} data-testid={`view-basic-${it.id}`}>
                    <Eye size={16} className="text-[#0A2540]" />
                  </Button>
                  {/* Edit visible only to admin/cpa/document_team OR to the client while form is still a draft. */}
                  {((canEditClient) || (!isStaff && !submitted)) && (
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Edit" onClick={() => openEdit(it)} data-testid={`edit-basic-${it.id}`}>
                      <PencilSimple size={16} className="text-[#0A2540]" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* FORM DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit Basic Information" : "US Tax Client Information Form"}
            </DialogTitle>
            <DialogDescription>
              Complete all sections and submit. Once submitted, only your CPA or an administrator can make changes.
            </DialogDescription>
          </DialogHeader>
          <BasicInfoForm form={form} setForm={setForm} />
          <DialogFooter className="sticky bottom-0 bg-white/95 backdrop-blur pt-4 border-t tf-hairline -mx-6 px-6">
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="form-cancel">Cancel</Button>
            <Button
              className="bg-[#D32F2F] hover:bg-[#D32F2F]/90"
              onClick={() => setSubmitConfirm(true)}
              data-testid="form-submit"
            >
              <PaperPlaneTilt size={14} className="mr-1.5" /> Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SUBMIT CONFIRMATION */}
      <AlertDialog open={submitConfirm} onOpenChange={setSubmitConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit this form?</AlertDialogTitle>
            <AlertDialogDescription>
              {isStaff
                ? "This will mark the form as submitted. It can still be edited by staff after submission."
                : "Once submitted, only your CPA or an administrator can make further changes. Please make sure everything is accurate before continuing."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="submit-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doSubmit} className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="submit-confirm">
              Yes, submit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* READ-ONLY VIEW SHEET */}
      <Sheet open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="basic-info-detail-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-[#0A2540]">
              <Eye size={18} weight="fill" /> Basic Info · TY {viewing?.tax_year}
            </SheetTitle>
            <SheetDescription>
              {viewing?.is_submitted
                ? <>Submitted {(viewing?.submitted_at || viewing?.updated_at || "").split("T")[0]}. Read-only view.</>
                : <>Draft form (not yet submitted).</>}
            </SheetDescription>
          </SheetHeader>
          {viewing && (
            <div className="mt-6">
              <BasicInfoForm
                form={{ ...emptyBasicInfo(), ...viewing, dependents: (viewing.dependents || []).map(withStableKey) }}
                setForm={() => {}}
                readOnly
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
